import React, { useContext, useEffect, useRef, useState } from 'react';
import { Table, Button, Space, message, Input, TableColumnType, GetRef } from 'antd';
import { fetchMySubmissions } from '../../api';
import { AuthContext } from '../../AuthContext';
import { EditOutlined, SearchOutlined } from '@ant-design/icons';
import { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';

type InputRef = GetRef<typeof Input>;

interface DataType {
    key: string;
    referenceNumber: string;
    travelAgentName: string;
    travelAgentLicense: string;
    registrationNumber: string;
    travelAgentEmail: string;
    submitDate: string;
    arrivalDate?: string;
}

type DataIndex = keyof DataType;

const MySubmissionsPage = () => {
    const authContext = useContext(AuthContext);
    const { setLoggedIn } = authContext || {};
    const [mySubmissions, setMySubmissions] = React.useState([] as any[]);
    const [total, setTotal] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);
    const [loading, setLoading] = React.useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [globalSearchText, setGlobalSearchText] = useState('');
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>(undefined);
    const searchInput = useRef<InputRef>(null);

    async function fetchData(page: number = currentPage, limit: number = pageSize, search?: string, sortFieldParam?: string, sortOrderParam?: string) {
        setLoading(true);
        try {
            const searchParam = search !== undefined ? search : globalSearchText;
            const sortFieldToUse = sortFieldParam !== undefined ? sortFieldParam : sortField;
            const sortOrderToUse = sortOrderParam !== undefined ? sortOrderParam : sortOrder;
            const response = await fetchMySubmissions(page, limit, searchParam || undefined, sortFieldToUse, sortOrderToUse);
            if (response.status === 401) {
                if (setLoggedIn) {
                    setLoggedIn(false);
                }
            }
            else if (response.status !== 200) {
                console.log('Failed to fetch submissions');
                return;
            }
            const result = await response.json();
            if (!result.success) {
                console.log('Failed to fetch submissions');
                return;
            }
            setMySubmissions(result.data);
            setTotal(result.total || 0);
            setCurrentPage(result.page || page);
            setPageSize(result.limit || limit);
        } catch (err) {
            console.log('Failed to fetch submissions');
            message.error('無法取得提交紀錄');
            setMySubmissions([]);
        } finally {
            setLoading(false);
        }
    }

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
        setGlobalSearchText(selectedKeys[0]);
        // Trigger fetchData with search term
        fetchData(1, pageSize, selectedKeys[0]);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setGlobalSearchText('');
        // Trigger fetchData without search term
        fetchData(1, pageSize, '');
    };

    const getColumnTitle = (dataIndex: DataIndex): string => {
        const titles: Record<DataIndex, string> = {
            key: '鍵',
            referenceNumber: '參考編號',
            travelAgentName: '持牌旅行代理商名稱',
            travelAgentLicense: '旅行代理商牌照號碼',
            registrationNumber: '登記貼紙編號',
            travelAgentEmail: '電郵地址',
            submitDate: '提交時間',
            arrivalDate: '抵港日期',
        };
        return titles[dataIndex] || dataIndex;
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`搜尋 ${getColumnTitle(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        搜尋
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        重設
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        關閉
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => (searchInput as any).current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: any[] = [
        {
            title: '參考編號',
            dataIndex: 'referenceNumber',
            key: 'referenceNumber',
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('referenceNumber'),
            render: (text: string) => (
                <span style={{ fontWeight: '500', color: '#2c3e50' }}>{text}</span>
            ),
        },
        {
            title: '持牌旅行代理商名稱',
            dataIndex: 'travelAgentName',
            key: 'travelAgentName',
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('travelAgentName'),
            render: (text: string) => (
                <span style={{ color: '#2c3e50' }}>{text}</span>
            ),
        },
        {
            title: '旅行代理商牌照號碼',
            dataIndex: 'travelAgentLicense',
            key: 'travelAgentLicense',
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('travelAgentLicense'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '登記貼紙編號',
            dataIndex: 'registrationNumber',
            key: 'registrationNumber',
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('registrationNumber'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '電郵地址',
            dataIndex: 'travelAgentEmail',
            key: 'travelAgentEmail',
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('travelAgentEmail'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '抵港日期',
            dataIndex: 'arrivalDate',
            key: 'arrivalDate',
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('arrivalDate'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '提交時間',
            dataIndex: 'submitDate',
            key: 'submitDate',
            sorter: true,
            showSorterTooltip: false,
            ...getColumnSearchProps('submitDate'),
            render: (text: string) => (
                <span style={{ color: '#5a6c7d' }}>{text}</span>
            ),
        },
        {
            title: '動作',
            key: 'action',
            render: (text: string, record: any) => (
                <Space size="middle">
                    <Button 
                        type="primary" 
                        icon={<EditOutlined />} 
                        onClick={() => handleEdit(record.id)}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                        }}
                    >
                        更改
                    </Button>
                </Space>
            ),
        }
    ];

    const handleEdit = (key: string) => {
        const item = mySubmissions.find((item) => item.id === key);
        const encoded = btoa(JSON.stringify({
            referenceNumber: item.referenceNumber,
            registrationNumber: item.registrationNumber,
            travelAgentEmail: item.travelAgentEmail,
        }));
        window.open(`/form/${encoded}`, '_blank');
    };

    useEffect(() => {
        fetchData();
    }, []);


    return (
        <div style={{ padding: '32px', maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{ 
                marginBottom: '24px',
                padding: '20px 24px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
                <h2 style={{ 
                    margin: 0,
                    fontSize: '26px',
                    fontWeight: '700',
                    color: '#1a1a1a',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                }}>提交紀錄</h2>
            </div>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}>
                <Table 
                    rowKey="id" 
                    columns={columns} 
                    dataSource={mySubmissions}
                    size="small"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: total,
                        pageSizeOptions: ['10', '20', '30', '50'],
                        showSizeChanger: true,
                        showTotal: (total) => `總共 ${total} 條記錄`,
                    }}
                    onChange={(pagination, filters, sorter: any) => {
                        // Handle pagination changes
                        const newPage = pagination.current || currentPage;
                        const newPageSize = pagination.pageSize || pageSize;
                        
                        // Handle sorting
                        let newSortField = sortField;
                        let newSortOrder = sortOrder;
                        
                        if (sorter && sorter.field) {
                            newSortField = sorter.field as string;
                            newSortOrder = sorter.order as 'ascend' | 'descend' | undefined;
                            setSortField(newSortField);
                            setSortOrder(newSortOrder);
                        } else if (sorter && !sorter.order) {
                            // Clear sorting
                            newSortField = undefined;
                            newSortOrder = undefined;
                            setSortField(undefined);
                            setSortOrder(undefined);
                        }
                        
                        setCurrentPage(newPage);
                        setPageSize(newPageSize);
                        fetchData(newPage, newPageSize, globalSearchText, newSortField, newSortOrder);
                    }}
                />
            </div>
        </div>
    );
};

export default MySubmissionsPage;